import React, {
  useEffect, useMemo, useState, useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Button, Form, message, Modal, Upload, Tag,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { InboxOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { aiImportOrders, aiPreviewOrders, aiReanalyzeOrders } from '../../../store/actions/orders/aiImport.actions';
import { getPayloadData, isSuccess } from '../../../utils/responseUtils';
import ImportOrderPreviewStep from './ImportOrderPreviewStep';
import useImportOrderPreview, { normalizeOrdersForImport } from './useImportOrderPreview';

const Guidelines = () => {
  const { t } = useTranslation();
  return (
    <ul>
      <li>{t('ORDER.IMPORT_ORDER.AI_GUIDELINES.POINT_1')}</li>
      <li>{t('ORDER.IMPORT_ORDER.AI_GUIDELINES.POINT_2')}</li>
      <li>{t('ORDER.IMPORT_ORDER.AI_GUIDELINES.POINT_3')}</li>
    </ul>
  );
};

const AIImportOrderModal = ({
  visible, onCancel, onSuccess, contextCompanyId,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [progressIdx, setProgressIdx] = useState(0);
  const [analysisStatus, setAnalysisStatus] = useState('idle'); // idle | analyzing | timeout | error
  const cancelRef = useRef({ cancelled: false });
  const previewState = useImportOrderPreview({ visible, step });
  const { clearPreviewState } = previewState;

  const progressMessages = useMemo(() => {
    const arr = t('ORDER.IMPORT_ORDER.AI_PROGRESS_MESSAGES', { returnObjects: true });
    return Array.isArray(arr) ? arr.filter(Boolean) : [];
  }, [t]);

  useEffect(() => {
    if (!loading || step !== 1 || progressMessages.length <= 1) return undefined;
    setProgressIdx(0);
    const id = setInterval(() => {
      setProgressIdx((prev) => (prev + 1) % progressMessages.length);
    }, 3000);
    return () => clearInterval(id);
  }, [loading, step, progressMessages]);

  useEffect(() => {
    if (!visible) {
      setFileList([]);
      setStep(1);
      setAnalysisStatus('idle');
      cancelRef.current.cancelled = false;
      clearPreviewState();
      form.resetFields();
    }
  }, [visible, form, clearPreviewState]);

  const reanalyzeCurrentPreview = ({ showFailureMessage = true } = {}) => {
    const orders = normalizeOrdersForImport(previewState.getAllOrdersForValidation());
    if (!orders.length) {
      setLoading(false);
      return Promise.resolve(null);
    }

    setLoading(true);
    return dispatch(aiReanalyzeOrders(orders, contextCompanyId))
      .then((response) => {
        if (isSuccess(response)) {
          const data = getPayloadData(response) || {};
          previewState.applyPreviewData(data);
        } else if (showFailureMessage) {
          message.error(t('ORDER.IMPORT_ORDER.REANALYZE_FAILED'));
        }
        setLoading(false);
        return response;
      });
  };

  const handleOk = () => {
    if (step === 1) {
      if (fileList.length === 0) {
        form.validateFields();
        return;
      }
      cancelRef.current.cancelled = false;
      setAnalysisStatus('analyzing');
      setLoading(true);
      dispatch(aiPreviewOrders(fileList[0], contextCompanyId, { cancelRef }))
        .then((response) => {
          if (isSuccess(response)) {
            const data = getPayloadData(response) || {};
            previewState.applyPreviewData(data);
            setStep(2);
            setAnalysisStatus('idle');
          } else {
            const errMsg = response?.error?.message || '';
            if (errMsg === 'PREVIEW_TIMEOUT') {
              setAnalysisStatus('timeout');
            } else if (errMsg === 'CANCELLED') {
              setAnalysisStatus('idle');
            } else {
              setAnalysisStatus('error');
            }
          }
          setLoading(false);
        });
    } else if (step === 2) {
      if (previewState.fileLevelErrors.length) {
        message.warning(t('ORDER.IMPORT_ORDER.FILE_ERRORS_DESC'));
        return;
      }

      if (previewState.hasUnvalidatedChanges) {
        message.warning(t('ORDER.IMPORT_ORDER.REVALIDATION_REQUIRED_AFTER_EDITS'));
        return;
      }

      const ordersToImport = previewState.getOrdersToImport();
      if (!ordersToImport.length) {
        message.warning(t('COMMON.PLEASE_SELECT_AT_LEAST_ONE'));
        return;
      }
      const normalizedOrders = normalizeOrdersForImport(ordersToImport);
      setLoading(true);
      dispatch(aiImportOrders(normalizedOrders, contextCompanyId))
        .then((response) => {
          if (isSuccess(response)) {
            message.success(t('ORDER.IMPORT_ORDER.SUCCESS'));
            onSuccess();
            onCancel();
            setLoading(false);
            return;
          }
          reanalyzeCurrentPreview({ showFailureMessage: false });
        });
    }
  };

  const handleCancelClick = () => {
    if (loading && step === 1) {
      // Cancel only the polling/analysis on FE
      cancelRef.current.cancelled = true;
      setLoading(false);
      setAnalysisStatus('idle');
      return;
    }
    onCancel();
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const uploadProps = {
    name: 'file',
    multiple: false,
    fileList,
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error(t('ORDER.IMPORT_ORDER.AI_FILE_TOO_LARGE'));
        return Upload.LIST_IGNORE;
      }
      setFileList([file]);
      form.setFieldsValue({ file: [file] });
      return Upload.LIST_IGNORE;
    },
  };

  const okTextLabel = useMemo(() => {
    if (step === 1) {
      return analysisStatus === 'timeout' ? t('COMMON.TRY_AGAIN') : t('COMMON.NEXT');
    }
    return t('ORDER.IMPORT_ORDER.IMPORT_SELECTED');
  }, [step, analysisStatus, t]);

  return (
    <Modal
      className={`ai-import-order-modal${step === 2 ? ' aiio-preview-modal' : ''}`}
      destroyOnClose
      open={visible}
      title={(
        <span>
          {t('ORDER.IMPORT_WITH_AI')}
          <Tag className="beta-tag">{t('ORDER.IMPORT_ORDER.BETA')}</Tag>
        </span>
      )}
      onOk={handleOk}
      onCancel={handleCancelClick}
      confirmLoading={loading}
      okText={okTextLabel}
      width={step === 2 ? '96vw' : 800}
      style={step === 2 ? { maxWidth: 1560 } : undefined}
      okButtonProps={step === 2
        ? {
          disabled: previewState.fileLevelErrors.length > 0
                || previewState.selectedRowKeys.length === 0
                || previewState.hasUnvalidatedChanges
                || previewState.selectedRowKeys.some((k) => previewState.errorsByIndex.has(k)),
        } : undefined}
      footer={step === 2 ? (
        <div className="aiio-footer">
          <div>
            <Button onClick={() => setStep(1)}>
              {t('COMMON.BACK')}
            </Button>
          </div>
          <div className="aiio-flex-row aiio-align-center aiio-gap-8">
            <Button onClick={handleCancelClick} className="aiio-mr-8">
              {t('COMMON.CANCEL')}
            </Button>
            <Button onClick={() => reanalyzeCurrentPreview()} loading={loading}>
              {t('ORDER.IMPORT_ORDER.REVIEW_AGAIN')}
            </Button>
            <Button
              type="primary"
              onClick={handleOk}
              loading={loading}
              disabled={previewState.fileLevelErrors.length > 0
                || previewState.selectedRowKeys.length === 0
                || previewState.hasUnvalidatedChanges
                || previewState.selectedRowKeys.some((k) => previewState.errorsByIndex.has(k))}
            >
              {t('ORDER.IMPORT_ORDER.IMPORT_SELECTED')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="aiio-footer">
          <div />
          <div className="aiio-flex-row aiio-align-center aiio-gap-8">
            {loading && step === 1 && (
              <div className="aiio-progress aiio-nowrap">
                <span key={progressIdx} className="aiio-progress-text aiio-fw-500">
                  {progressMessages[progressIdx] || ''}
                </span>
              </div>
            )}
            <Button onClick={handleCancelClick} className="aiio-mr-8">
              {t('COMMON.CANCEL')}
            </Button>
            <Button type="primary" onClick={handleOk} loading={loading}>
              {t('COMMON.NEXT')}
            </Button>
          </div>
        </div>
      )}
    >
      {step === 1 && (
        <Form
          form={form}
          layout="vertical"
          name="aiImportOrder"
        >
          {(analysisStatus === 'timeout' || analysisStatus === 'error') && (
            <Alert
              message={<b>{analysisStatus === 'timeout' ? t('COMMON.WARNING') : t('COMMON.ERROR')}</b>}
              description={analysisStatus === 'timeout'
                ? t('ORDER.IMPORT_ORDER.AI_ANALYSIS_TIMEOUT_DESC')
                : t('ORDER.IMPORT_ORDER.AI_ANALYSIS_ERROR_DESC')}
              type={analysisStatus === 'timeout' ? 'warning' : 'error'}
              showIcon
              className="aiio-mb-16"
            />
          )}
          <Form.Item
            name="file"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            rules={[{ required: true, message: t('ORDER.IMPORT_ORDER.SELECT_FILE') }]}
          >
            <Upload.Dragger
              name={uploadProps.name}
              multiple={uploadProps.multiple}
              fileList={uploadProps.fileList}
              onRemove={uploadProps.onRemove}
              beforeUpload={uploadProps.beforeUpload}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">{t('ORDER.IMPORT_ORDER.DRAG_AND_DROP')}</p>
            </Upload.Dragger>
          </Form.Item>
          <Alert
            message={<b>{t('ORDER.IMPORT_ORDER.AI_GUIDELINES.TITLE')}</b>}
            description={<Guidelines />}
            type="info"
            showIcon
            className="aiio-mb-24"
          />
        </Form>
      )}
      {step === 2 && (
        <ImportOrderPreviewStep previewState={previewState} showAiVehiclesTooltip />
      )}
    </Modal>
  );
};

AIImportOrderModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  contextCompanyId: PropTypes.string.isRequired,
};

export default AIImportOrderModal;
